import { supabase } from './supabase';
import { triggerBookingAutomations } from './automationsV2';
import { generateAndUploadPDF } from './newPdfGenerator';

export interface Payment {
  id: string;
  business_id: string;
  booking_id: string;
  amount: number;
  tip_amount?: number;
  base_amount?: number;
  method: 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'voided';
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  pdf_url?: string;
  payment_token?: string;
  online_payment_url?: string;
  stripe_payment_intent_id?: string;
  stripe_checkout_session_id?: string;
  booking_number?: number;
  booking_title?: string;
  booking_total?: number;
  booking_subtotal?: number;
  booking_tax_amount?: number;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  booking_services?: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    tax_amount: number;
    total_amount: number;
    duration_minutes: number;
    vehicle_make: string;
    vehicle_model: string;
  }[];
  booking_discounts?: {
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }[];
}

export async function getPayments(businessId: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments_with_details')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Payment[];
}

async function getBookingPayments(bookingId: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments_with_stripe_details')
    .select('*')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Payment[];
}

export const createPayment = async ({
  business_id,
  booking_id,
  amount,
  method,
  status = 'completed',
  reference = null,
  notes = null
}: {
  business_id: string;
  booking_id: string;
  amount: number;
  method: 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other';
  status?: 'pending' | 'completed' | 'failed' | 'refunded' | 'voided';
  reference?: string | null;
  notes?: string | null;
}): Promise<Payment> => {
  console.log('💰 [PAYMENT DEBUG] Starting payment creation...');
  console.log('💰 [PAYMENT DEBUG] Payment params:', {
    business_id,
    booking_id,
    amount,
    method,
    status,
    reference,
    notes
  });

  if (!supabase) {
    console.error('❌ [PAYMENT DEBUG] Supabase client not initialized');
    throw new Error('Supabase client not initialized');
  }

  console.log('💰 [PAYMENT DEBUG] Creating payment record in database...');
  const { data: payment, error } = await supabase
    .from('payments')
    .insert({
      business_id,
      booking_id,
      amount,
      method,
      status,
      reference,
      notes
    })
    .select()
    .single();

  console.log('💰 [PAYMENT DEBUG] Payment creation result:', { payment, error });

  if (error) {
    console.error('❌ [PAYMENT DEBUG] Payment creation failed:', error);
    throw new Error(`Failed to create payment: ${error.message}`);
  }

  console.log('✅ [PAYMENT DEBUG] Payment created successfully with ID:', payment.id);

  // Check if this is the first real payment and handle unpaid invoice replacement
  console.log('🔍 [PAYMENT DEBUG] Checking for unpaid invoice to replace...');
  try {
    // Look for existing unpaid invoice (pending status with 0 amount)
    const { data: unpaidInvoices, error: unpaidError } = await supabase
      .from('payments')
      .select('*')
      .eq('booking_id', booking_id)
      .eq('status', 'pending')
      .eq('amount', 0)
      .eq('reference', 'unpaid-invoice');

    if (!unpaidError && unpaidInvoices && unpaidInvoices.length > 0) {
      console.log('📄 [PAYMENT DEBUG] Found unpaid invoice to replace:', unpaidInvoices[0].id);
      
      // Delete the unpaid invoice payment record and its PDF
      const unpaidInvoice = unpaidInvoices[0];
      
      // Try to delete the PDF file from storage if it exists
      if (unpaidInvoice.pdf_url) {
        try {
          const urlParts = unpaidInvoice.pdf_url.split('/');
          const filename = urlParts[urlParts.length - 1];
          console.log('🗑️ [PAYMENT DEBUG] Deleting unpaid invoice PDF:', filename);
          
          await supabase.storage
            .from('receipts')
            .remove([filename]);
          
          console.log('✅ [PAYMENT DEBUG] Unpaid invoice PDF deleted successfully');
        } catch (deleteError) {
          console.error('❌ [PAYMENT DEBUG] Failed to delete unpaid invoice PDF:', deleteError);
        }
      }
      
      // Delete the unpaid payment record
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('id', unpaidInvoice.id);

      if (deleteError) {
        console.error('❌ [PAYMENT DEBUG] Failed to delete unpaid invoice record:', deleteError);
      } else {
        console.log('✅ [PAYMENT DEBUG] Unpaid invoice record deleted successfully');
      }
    } else {
      console.log('ℹ️ [PAYMENT DEBUG] No unpaid invoice found - this is a subsequent payment');
    }
  } catch (replaceError) {
    console.error('❌ [PAYMENT DEBUG] Error during unpaid invoice replacement:', replaceError);
    // Continue without throwing - payment was created successfully
  }

  // Generate PDF invoice for the payment (non-blocking)
  console.log('📄 [PAYMENT DEBUG] Starting PDF generation process...');
  console.log('📄 [PAYMENT DEBUG] PDF generation params:', {
    paymentId: payment.id,
    bookingId: booking_id,
    businessId: business_id
  });

  try {
    console.log('📄 [PAYMENT DEBUG] Calling generateAndUploadPDF...');
    
    // Fetch complete booking data with relations
    console.log('📄 [PAYMENT DEBUG] Fetching booking data for:', booking_id);
    const { data: bookingData, error: bookingError } = await supabase
      .from('bookings')
      .select(`
        *,
        customer_id,
        customers (*),
        booking_services (
          *,
          services (*)
        )
      `)
      .eq('id', booking_id)
      .single();

    if (bookingError || !bookingData) {
      console.error('❌ [PAYMENT DEBUG] Failed to fetch booking data:', bookingError);
      throw new Error(`Failed to fetch booking data: ${bookingError?.message}`);
    }

    console.log('✅ [PAYMENT DEBUG] Booking data fetched:', bookingData);

    // Fetch business data
    console.log('📄 [PAYMENT DEBUG] Fetching business data for:', business_id);
    const { data: businessData, error: businessError } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', business_id)
      .single();

    if (businessError || !businessData) {
      console.error('❌ [PAYMENT DEBUG] Failed to fetch business data:', businessError);
      throw new Error(`Failed to fetch business data: ${businessError?.message}`);
    }

    console.log('✅ [PAYMENT DEBUG] Business data fetched:', businessData);

    // Get customer data (from booking relation or separate fetch)
    let customerData = bookingData.customers;
    if (!customerData && bookingData.customer_id) {
      console.log('📄 [PAYMENT DEBUG] Fetching customer data for:', bookingData.customer_id);
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', bookingData.customer_id)
        .single();

      if (!customerError && customer) {
        customerData = customer;
        console.log('✅ [PAYMENT DEBUG] Customer data fetched:', customerData);
      } else {
        console.error('❌ [PAYMENT DEBUG] Failed to fetch customer data:', customerError);
      }
    }

    // Fetch ALL payments for this booking to calculate cumulative total
    console.log('📄 [PAYMENT DEBUG] Fetching all payments for booking to calculate cumulative total...');
    const { data: allBookingPayments, error: paymentsError } = await supabase
      .from('payments')
      .select('*')
      .eq('booking_id', booking_id)
      .eq('status', 'completed')
      .order('created_at', { ascending: true });

    if (paymentsError) {
      console.error('❌ [PAYMENT DEBUG] Failed to fetch booking payments:', paymentsError);
      throw new Error(`Failed to fetch booking payments: ${paymentsError?.message}`);
    }

    // Calculate cumulative payment total (up to and including current payment)
    const cumulativePaymentTotal = allBookingPayments.reduce((sum, p) => sum + p.amount, 0);
    console.log('💰 [PAYMENT DEBUG] Payment calculation:', {
      currentPayment: payment.amount,
      allPayments: allBookingPayments.map(p => ({ id: p.id, amount: p.amount, created: p.created_at })),
      cumulativeTotal: cumulativePaymentTotal
    });

    // Prepare payment data with cumulative total
    const paymentDataForPDF = {
      id: payment.id,
      amount: cumulativePaymentTotal, // Use cumulative total instead of individual payment
      currentPaymentAmount: payment.amount, // Keep track of current payment for reference
      method: payment.method,
      status: payment.status,
      reference: payment.reference,
      notes: payment.notes,
      created_at: payment.created_at
    };

    // Prepare comprehensive booking data
    const bookingDataForPDF = {
      booking_number: bookingData.booking_number,
      title: bookingData.title || bookingData.service_name,
      service_name: bookingData.service_name,
      description: bookingData.description,
      service_description: bookingData.service_description,
      vehicle: bookingData.vehicle,
      grand_total: bookingData.grand_total,
      total_amount: bookingData.total_amount,
      subtotal: bookingData.subtotal,
      tax_amount: bookingData.tax_amount,
      tax_rate: bookingData.tax_rate,
      tax_type: bookingData.tax_type,
      discount_amount: bookingData.discount_amount,
      discount_percentage: bookingData.discount_percentage,
      services: bookingData.services || [],
      booking_services: bookingData.booking_services || []
    };

    console.log('📄 [PAYMENT DEBUG] Prepared data for PDF generation:', {
      payment: paymentDataForPDF,
      booking: bookingDataForPDF,
      business: businessData,
      customer: customerData
    });

    const pdfUrl = await generateAndUploadPDF(
      payment.id,
      paymentDataForPDF,
      bookingDataForPDF,
      businessData,
      customerData || { full_name: 'Customer', email: 'customer@example.com' }
    );
    console.log('✅ [PAYMENT DEBUG] PDF generated successfully:', pdfUrl);

    // Update payment with PDF URL
    console.log('💰 [PAYMENT DEBUG] Updating payment with PDF URL...');
    const { data: updatedPayment, error: updateError } = await supabase
      .from('payments')
      .update({ pdf_url: pdfUrl })
      .eq('id', payment.id)
      .select()
      .single();

    console.log('💰 [PAYMENT DEBUG] Payment update result:', { updatedPayment, updateError });

    if (updateError) {
      console.error('❌ [PAYMENT DEBUG] Failed to update payment with PDF URL:', updateError);
      // Continue without throwing - payment was created successfully
    } else {
      console.log('✅ [PAYMENT DEBUG] Payment updated with PDF URL successfully');
      // Return updated payment with PDF URL
      payment.pdf_url = pdfUrl;
    }
  } catch (pdfError) {
    console.error('❌ [PAYMENT DEBUG] PDF generation failed:', pdfError);
    console.error('❌ [PAYMENT DEBUG] PDF error details:', {
      message: pdfError instanceof Error ? pdfError.message : 'Unknown error',
      stack: pdfError instanceof Error ? pdfError.stack : 'No stack trace'
    });
    // Continue without throwing - payment was created successfully
    console.log('⚠️ [PAYMENT DEBUG] Payment created but PDF generation failed - continuing...');
  }

  // Trigger automations - trigger a "paid" event since payment was recorded
  console.log('🤖 [PAYMENT DEBUG] Triggering booking automations...');
  try {
    // Prepare booking data for automation
    const bookingData = {
      booking_id: booking_id,
      payment_id: payment.id,
      payment_amount: amount,
      payment_method: method,
      payment_status: status,
      triggered_by: 'payment_creation'
    };
    
    await triggerBookingAutomations(business_id, 'paid', bookingData);
    console.log('✅ [PAYMENT DEBUG] Booking automations triggered successfully');
  } catch (automationError) {
    console.error('❌ [PAYMENT DEBUG] Failed to trigger automations:', automationError);
    // Continue without throwing - payment was created successfully
  }

  console.log('🎉 [PAYMENT DEBUG] Payment creation process completed successfully');
  return payment;
};

export async function updatePayment(id: string, updates: Partial<Payment>) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}

async function deletePayment(id: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { error } = await supabase
    .from('payments')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function voidPayment(id: string, reason: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'voided',
      notes: reason
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}

/**
 * Completely void/delete a payment - removes payment record and PDF entirely
 */
export async function completelyVoidPayment(paymentId: string, reason: string): Promise<void> {
  console.log('🗑️ [VOID DEBUG] Starting complete void process for payment:', paymentId);
  
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    // Step 1: Get the payment record to access PDF URL
    console.log('🔍 [VOID DEBUG] Fetching payment record...');
    const { data: payment, error: fetchError } = await supabase
      .from('payments')
      .select('*')
      .eq('id', paymentId)
      .single();

    if (fetchError) {
      console.error('❌ [VOID DEBUG] Failed to fetch payment:', fetchError);
      throw new Error(`Failed to fetch payment: ${fetchError.message}`);
    }

    if (!payment) {
      console.error('❌ [VOID DEBUG] Payment not found');
      throw new Error('Payment not found');
    }

    console.log('✅ [VOID DEBUG] Payment found:', {
      id: payment.id,
      amount: payment.amount,
      pdf_url: payment.pdf_url,
      booking_id: payment.booking_id
    });

    // Step 2: Delete PDF from storage if it exists
    if (payment.pdf_url) {
      try {
        console.log('🗑️ [VOID DEBUG] Deleting PDF from storage...');
        
        // Extract filename from URL
        const urlParts = payment.pdf_url.split('/');
        const filename = urlParts[urlParts.length - 1];
        
        console.log('🗑️ [VOID DEBUG] PDF filename to delete:', filename);
        
        const { error: storageError } = await supabase.storage
          .from('receipts')
          .remove([filename]);

        if (storageError) {
          console.error('❌ [VOID DEBUG] Failed to delete PDF:', storageError);
          // Continue anyway - we still want to delete the payment record
        } else {
          console.log('✅ [VOID DEBUG] PDF deleted successfully');
        }
      } catch (pdfError) {
        console.error('❌ [VOID DEBUG] Error deleting PDF:', pdfError);
        // Continue anyway
      }
    } else {
      console.log('ℹ️ [VOID DEBUG] No PDF to delete');
    }

    // Step 3: Delete the payment record completely
    console.log('🗑️ [VOID DEBUG] Deleting payment record...');
    const { error: deleteError } = await supabase
      .from('payments')
      .delete()
      .eq('id', paymentId);

    if (deleteError) {
      console.error('❌ [VOID DEBUG] Failed to delete payment record:', deleteError);
      throw new Error(`Failed to delete payment: ${deleteError.message}`);
    }

    console.log('✅ [VOID DEBUG] Payment record deleted successfully');
    
    // Step 4: Log the void action for audit purposes
    console.log('📝 [VOID DEBUG] Payment completely voided:', {
      payment_id: paymentId,
      amount: payment.amount,
      booking_id: payment.booking_id,
      reason: reason,
      voided_at: new Date().toISOString()
    });

    console.log('🎉 [VOID DEBUG] Complete void process finished successfully');

  } catch (error) {
    console.error('❌ [VOID DEBUG] Complete void process failed:', error);
    throw error;
  }
}

async function refundPayment(id: string, amount: number, reason: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'refunded',
      notes: reason
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}